import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Error boundary that catches TipTap/React DOM reconciliation conflicts
 * (`removeChild` / `insertBefore` failures) and silently remounts the
 * editor subtree.
 *
 * This is a known interaction: TipTap manipulates DOM nodes directly
 * while React is also tracking them during unmount/remount cycles. The
 * fix is to swallow the specific error type and bump an internal key so
 * the subtree is recreated from scratch.
 */
export class EditorErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode; className?: string },
  { hasError: boolean; errorKey: number }
> {
  constructor(props: { children: ReactNode; fallback?: ReactNode; className?: string }) {
    super(props)
    this.state = { hasError: false, errorKey: 0 }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (
      error.message?.includes('removeChild') ||
      error.message?.includes('insertBefore')
    ) {
      // Auto-recover by bumping the key to force remount.
      this.setState((s) => ({ hasError: false, errorKey: s.errorKey + 1 }))
    } else {
      console.error('[EditorErrorBoundary] Non-DOM error:', error, info)
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null
    }
    return <div className={this.props.className} key={this.state.errorKey}>{this.props.children}</div>
  }
}
